import React, {FC, useEffect, useMemo, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {toast, ToastContainer} from 'react-toastify'
import {getCompanyAdminList, getCompanyList} from '../../Management/core/_requests'
import {FileViewerModal} from '../components/FileViewerModal'
import AuditFindingAttachmentsModal from './AuditFindingAttachmentsModal'

import type {Vessel} from '../../Management/core/_models'
import type {
  QHSEAuditFindingDto,
  AuditFindingRecord,
  FindingStatus,
  AuditFindingAttachmentInfo,
  FindingType,
} from '../core/_models'
import type {AuditDto} from '../core/_models'
import {
  searchAuditFindings,
  createAuditFinding,
  getAuditFinding,
  updateAuditFinding,
  uploadAuditFindingAttachments,
  listAuditFindingAttachments,
  AuditFindingAttachmentViewUrl,
  AuditFindingAttachmentDownloadUrl,
  searchAudits,
  deleteAuditFindingAttachment,
  AuditFindingAttachmentFileViewUrl,
  AuditFindingAttachmentFileDownloadUrl,
  closeAuditFinding,
  reopenAuditFinding,
  submitAuditFinding,
  approveAuditFinding,
  rejectAuditFinding,
  deleteAuditFinding,
} from '../core/_requests'

import AddAuditFindingModal from './AddAuditFindingModal'
import {ViewAuditFindingModal} from './ViewAuditFindingModal'
import EditAuditFindingModal from './EditAuditFindingModal'
import FindingApprovalActionModal, {ApprovalAction} from './FindingApprovalActionModal'
import DeleteRecordConfirmModal from './DeleteRecordConfirmModal'
import type {AuditFindingSubmitShape} from '../core/_models'
import {QHSE_PERMS} from '../core/permissions'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import {
  filterVesselsForManagementList,
  getUserCompanyGroupAdminId,
  isBroadFleetPickerUser,
  isLikelyVesselAssignedUser,
} from '../core/qhseUserContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'

const toastApiError = (e: any, fallback: string) => {
  if (e?.response?.status === 403) {
    toast.error('You do not have access to perform this action.', {position: 'top-center'})
    return
  }
  const msg = e?.response?.data?.message || e?.message || fallback
  toast.error(msg, {position: 'top-center'})
}

const stripHtml = (html?: string | null): string =>
  html ? html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : ''

type CompanyGroup = {id: number; name: string}
type Subcompany = {id: number; name: string; companyId: number}

const tableOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(255,255,255,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 5,
}

const spinner = (
  <div className='spinner-border' role='status' aria-label='Loading'>
    <span className='visually-hidden'>Loading...</span>
  </div>
)

const niceEnum = (s?: string | null) =>
  s ? s.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-'

const statusBadgeClass = (status?: FindingStatus | null) => {
  switch (status) {
    case 'SATISFACTORY':
      return 'badge-light-success'
    case 'NON_CRITICAL':
      return 'badge-light-primary'
    case 'CRITICAL':
      return 'badge-light-danger'
    case 'OBSERVATION':
      return 'badge-light-warning'
    case 'OPPORTUNITY_FOR_IMPROVEMENT':
      return 'badge-light-info'
    default:
      return 'badge-light'
  }
}

const AuditFindingIndex: FC = () => {
  const {currentUser, can, permissionsLoaded, isSuperAdmin} = useAuth()

  const shipboardUser = isLikelyVesselAssignedUser(currentUser)
  const broadFleet = isBroadFleetPickerUser(currentUser)
  const myCgaId = getUserCompanyGroupAdminId(currentUser)
  const isTopLevel = broadFleet
  const effectiveCompanyId = isTopLevel ? undefined : (myCgaId ?? undefined)
  const profileVesselId = currentUser?.vessel?.id

  const canReadFinding = permissionsLoaded && can(QHSE_PERMS.AUDIT_FINDING_READ)
  const canCreateFinding = permissionsLoaded && can(QHSE_PERMS.AUDIT_FINDING_CREATE)
  const canUpdateFinding = permissionsLoaded && can(QHSE_PERMS.AUDIT_FINDING_UPDATE)
  const canSubmitFinding = permissionsLoaded && can(QHSE_PERMS.AUDIT_FINDING_SUBMIT)
  const canApproveFinding = permissionsLoaded && can(QHSE_PERMS.AUDIT_FINDING_APPROVE)
  const canRejectFinding = permissionsLoaded && can(QHSE_PERMS.AUDIT_FINDING_REJECT)
  const canDeleteFinding = isSuperAdmin || (permissionsLoaded && can(QHSE_PERMS.AUDIT_FINDING_DELETE))
  const canOpenFindingFiles =
    permissionsLoaded &&
    (can(QHSE_PERMS.AUDIT_FINDING_ATTACH_VIEW) ||
      can(QHSE_PERMS.AUDIT_FINDING_ATTACH_DOWNLOAD))

  // filters
  const [filters, setFilters] = useState({
    companyId: '',
    subcompanyId: '',
    vesselId: '',
    status: 'All',        // All / enum from FindingStatus
    findingType: 'All',   // All / enum from FindingType
    fromDate: '',
    toDate: '',
  })
  const [searchTerm, setSearchTerm] = useState('')

  // lookups
  const [companies, setCompanies] = useState<CompanyGroup[]>([])
  const [subcompanies, setSubcompanies] = useState<Subcompany[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [vesselsScoped, setVesselsScoped] = useState<Vessel[]>([])
  const [vesselMetaMap, setVesselMetaMap] = useState<
    Record<number, {name: string; companyGroupAdminId?: number | null; companyAdminId?: number | null}>
  >({})

  const [lookupsReady, setLookupsReady] = useState(false)
  const [loadingLookups, setLoadingLookups] = useState(false)

  // Audit dropdown for Add/Edit
  const [auditOptions, setAuditOptions] = useState<{id: number; label: string}[]>([])
  const [auditOptionsLoading, setAuditOptionsLoading] = useState(false)
  const [addVesselId, setAddVesselId] = useState('')

  // data
  const [records, setRecords] = useState<AuditFindingRecord[]>([])
  const [loading, setLoading] = useState(false)

  const [bootLoading, setBootLoading] = useState(true)
  const [noDataDelayPassed, setNoDataDelayPassed] = useState(false)

  // pagination / sorting
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortColumn, setSortColumn] = useState<keyof AuditFindingRecord | ''>('findingDate')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // modals
  const [addOpen, setAddOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [viewing, setViewing] = useState<QHSEAuditFindingDto | null>(null)
  const [editing, setEditing] = useState<QHSEAuditFindingDto | null>(null)
  const [approvalModalOpen, setApprovalModalOpen] = useState(false)
  const [approvalAction, setApprovalAction] = useState<ApprovalAction>('approve')
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [findingToDelete, setFindingToDelete] = useState<AuditFindingRecord | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [approvalTargetFinding, setApprovalTargetFinding] = useState<AuditFindingRecord | null>(null)
  const [approvalLoading, setApprovalLoading] = useState(false)
  const [creatingFinding, setCreatingFinding] = useState(false)
  const [submittingActionId, setSubmittingActionId] = useState<number | null>(null)

  const activeAuditVesselId = useMemo(() => {
    if (editOpen && editing?.vesselId) return String(editing.vesselId)
    if (addOpen) return addVesselId
    return ''
  }, [editOpen, editing?.vesselId, addOpen, addVesselId])

  // file viewer (single file)
  const [fileModal, setFileModal] = useState<{
    visible: boolean
    title: string
    fileName?: string
    viewUrl?: string
    downloadUrl?: string
  }>({
    visible: false,
    title: '',
  })

  // multi-attachment list
  const [attachmentsModal, setAttachmentsModal] = useState<{
    visible: boolean
    findingId: number | null
    findingLabel: string | null
    loading: boolean
    items: {fileName: string; viewUrl: string; downloadUrl: string}[]
  }>({
    visible: false,
    findingId: null,
    findingLabel: null,
    loading: false,
    items: [],
  })

  // -----------------------
  // Lookups: Company / Subcompany / Vessels
  // -----------------------
  useEffect(() => {
    const loadLookups = async () => {
      setLoadingLookups(true)
      try {
        const [groupsRaw, adminsRaw, vesselList] = await Promise.all([
          getCompanyAdminList?.().catch(() => []),
          getCompanyList?.().catch(() => []),
          fetchAccessibleVesselsForCurrentUser().then(normalizeAccessibleVessels).catch(() => [] as Vessel[]),
        ])

        const groups: CompanyGroup[] = Array.isArray(groupsRaw)
          ? groupsRaw.map((g: any) => ({
              id: Number(g.id),
              name: g.name ?? g.companyGroupAdminName ?? `Group #${g.id}`,
            }))
          : []

        const admins: Subcompany[] = Array.isArray(adminsRaw)
          ? adminsRaw.map((a: any) => ({
              id: Number(a.id),
              name: a.name ?? a.companyAdminName ?? `Company #${a.id}`,
              companyId: Number(
                a.cgaid?.id ??
                  a.cga?.id ??
                  a.companyGroupAdminId ??
                  a.companyGroupId
              ),
            }))
          : []

        setCompanies(groups.filter(g => Number.isFinite(g.id)))
        setSubcompanies(admins.filter(a => Number.isFinite(a.id) && Number.isFinite(a.companyId)))

        const vesselsForUser = filterVesselsForManagementList(vesselList, currentUser)

        setVessels(vesselList)
        setVesselsScoped(vesselsForUser)

        // meta map for vesselName + tenant ids
        const meta: Record<number, {name: string; companyGroupAdminId?: number | null; companyAdminId?: number | null}> = {}
        for (const v of vesselList as any[]) {
          const id = Number(v.id)
          meta[id] = {
            name: v.fleet_name || v.name || `Vessel ${id}`,
            companyGroupAdminId: Number(
              v.companyGroupAdmin?.id ??
                v.companyGroupId ??
                v.cgaid?.id
            ) || null,
            companyAdminId: Number(
              v.companyAdmin?.id ??
                v.companyId
            ) || null,
          }
        }
        setVesselMetaMap(meta)
      } catch (e) {
        console.error('Failed to load lookups (findings)', e)
        setCompanies([])
        setSubcompanies([])
        setVessels([])
        setVesselsScoped([])
        setVesselMetaMap({})
      } finally {
        setLoadingLookups(false)
        setLookupsReady(true)
      }
    }

    loadLookups()
  }, [currentUser])

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    if (globalState.searchQuery !== undefined) {
      setSearchTerm(globalState.searchQuery)
    }
  }, [globalState.searchQuery])

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      vesselId: globalState.vessel?.id ? String(globalState.vessel.id) : '',
      companyId: globalState.company ? String(globalState.company.id) : '',
    }))
  }, [globalState.vessel, globalState.company])

  // derived: vessels filtered by company / subcompany
  const vesselsForFilters = useMemo(() => {
    let list = vesselsScoped
    if (filters.companyId) {
      const cid = Number(filters.companyId)
      list = list.filter((v: any) =>
        Number(v.companyGroupAdmin?.id ?? v.companyGroupId) === cid
      )
    }
    if (filters.subcompanyId) {
      const scid = Number(filters.subcompanyId)
      list = list.filter((v: any) =>
        Number(v.companyAdmin?.id ?? v.companyId) === scid
      )
    }
    return list
  }, [vesselsScoped, filters.companyId, filters.subcompanyId])

  const subcompaniesForChosenCompany = useMemo(() => {
    if (!filters.companyId) return []
    const cid = Number(filters.companyId)
    return subcompanies.filter(sc => sc.companyId === cid)
  }, [filters.companyId, subcompanies])

  const hasSubcompaniesForChosenCompany = subcompaniesForChosenCompany.length > 0

  // initialize vessel selection for Add modal
  useEffect(() => {
    if (!addOpen) return
    if (shipboardUser) {
      setAddVesselId(profileVesselId ? String(profileVesselId) : '')
      return
    }
    setAddVesselId('')
  }, [addOpen, shipboardUser, profileVesselId])

  // -----------------------
  // Load Audit options for dropdown (finalized Audits)
  // -----------------------
  const loadAuditOptions = async (vesselId?: string) => {
    const vId = Number(vesselId)
    if (!vesselId || !Number.isFinite(vId)) {
      setAuditOptions([])
      setAuditOptionsLoading(false)
      return
    }

    setAuditOptionsLoading(true)
    setAuditOptions([])
    try {
      const res = await searchAudits({
        companyGroupId: filters.companyId
          ? Number(filters.companyId)
          : effectiveCompanyId,
        companyId: filters.subcompanyId ? Number(filters.subcompanyId) : undefined,
        vesselId: vId,
        isFinalized: true,
        activeOnly: true,
      })

      const list: AuditDto[] = Array.isArray(res?.content)
        ? res.content
        : (Array.isArray(res) ? res : [])

      const scoped = list.filter(d => Number(d.vesselId) === vId)

      const options = scoped.map((d: AuditDto) => {
        const vesselMeta = d.vesselId ? vesselMetaMap[d.vesselId] : undefined
        const vName = d.vesselName || vesselMeta?.name || '-'
        const labelParts = [
          vName,
          d.auditKindName || d.auditType || '',
          d.auditFromDate || d.auditToDate || '',
        ].filter(Boolean)

        return {
          id: d.id,
          label: labelParts.join(' — '),
        }
      })

      setAuditOptions(options)
    } catch (e) {
      console.error('Failed to load audit options', e)
      setAuditOptions([])
    } finally {
      setAuditOptionsLoading(false)
    }
  }

  useEffect(() => {
    if (!lookupsReady) return
    if (!activeAuditVesselId) {
      setAuditOptions([])
      setAuditOptionsLoading(false)
      return
    }
    loadAuditOptions(activeAuditVesselId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lookupsReady,
    filters.companyId,
    filters.subcompanyId,
    effectiveCompanyId,
    activeAuditVesselId,
  ])

  // -----------------------
  // Load findings
  // -----------------------
  const normalizeDate = (s?: string | null): string | null => {
    if (!s) return null
    return s.substring(0, 10)
  }

  /** we treat "finding date" in UI as the audit date now */
  const deriveAuditDate = (d: QHSEAuditFindingDto): string | null => {
    // Prefer AuditFromDate, fall back to AuditToDate
    const raw =
      (d as any).auditFromDate ||
      (d as any).auditToDate ||
      null

    if (!raw) return null
    return normalizeDate(String(raw))
  }

  const mapDtoToRecord = (d: QHSEAuditFindingDto): AuditFindingRecord => {
    const meta = d.vesselId ? vesselMetaMap[d.vesselId] : undefined

    const label = (d as any).findingLabel || d.findingName || ''

    const htmlDetails = d.description || d.findingName || ''
    const textDetails = stripHtml(htmlDetails)

    return {
      id: d.id,
      vesselName: d.vesselName || meta?.name || '-',
      auditName: d.auditKindName || d.auditType || '-',
      findingLabel: label,
      findingName: textDetails || label || '',
      critical: (d as any).critical ?? (d as any).isPositive ?? null,
      status: d.status,
      findingType: d.findingType ?? null,
      // this is now Audit DATE
      findingDate: deriveAuditDate(d),
      isClosed: d.isClosed ?? false,
      closedAt: d.closedAt ?? null,
      workflowStatus: d.workflowStatus ?? 'DRAFT',
      attachmentPath: d.attachmentPath || null,
      vesselId: d.vesselId ?? null,
      companyGroupAdminId:
        (d as any).companyGroupAdminId ?? meta?.companyGroupAdminId ?? null,
      companyAdminId:
        (d as any).companyAdminId ?? meta?.companyAdminId ?? null,
    }
  }

  const loadFindings = async () => {
    if (!lookupsReady) return
    setLoading(true)
    try {
      const params = {
        companyGroupId: filters.companyId ? Number(filters.companyId) : effectiveCompanyId,
        companyId: filters.subcompanyId ? Number(filters.subcompanyId) : undefined,
        vesselId: filters.vesselId ? Number(filters.vesselId) : undefined,
        status: filters.status === 'All' ? undefined : (filters.status as FindingStatus),
        findingType:
          filters.findingType === 'All'
            ? undefined
            : (filters.findingType as FindingType),
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        activeOnly: undefined,
      }

      const list = await searchAuditFindings(params)
      setRecords(list.map(mapDtoToRecord))
    } catch (e) {
      console.error('Failed to load audit findings', e)
      setRecords([])
      toastApiError(e, 'Failed to load audit findings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!lookupsReady) return
    ;(async () => {
      setBootLoading(true)
      try {
        await loadFindings()
      } finally {
        setBootLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupsReady, effectiveCompanyId])

  useEffect(() => {
    if (!lookupsReady) return
    loadFindings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.companyId,
    filters.subcompanyId,
    filters.vesselId,
    filters.status,
    filters.findingType,
    filters.fromDate,
    filters.toDate,
  ])

  useEffect(() => {
    if (bootLoading || loading) {
      setNoDataDelayPassed(false)
      return
    }
    const t = setTimeout(() => setNoDataDelayPassed(true), 500)
    return () => clearTimeout(t)
  }, [bootLoading, loading])

  // -----------------------
  // Handlers
  // -----------------------
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {name, value} = e.target
    setFilters(prev => {
      const next = {...prev, [name]: value}
      if (name === 'companyId') {
        next.subcompanyId = ''
        next.vesselId = ''
      }
      if (name === 'subcompanyId') {
        next.vesselId = ''
      }
      return next
    })
  }

  const handleClearFilters = () => {
    setFilters({
      companyId: '',
      subcompanyId: '',
      vesselId: '',
      status: 'All',
      findingType: 'All',
      fromDate: '',
      toDate: '',
    })
    setSearchTerm('')
  }

  const filteredData = useMemo(() => {
    return records.filter(r => {
      const matchesSearch =
        (r.vesselName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.auditName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.findingLabel || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.findingName || '').toLowerCase().includes(searchTerm.toLowerCase())

      const byCompany =
        !filters.companyId ||
        Number(r.companyGroupAdminId) === Number(filters.companyId)

      const bySubcompany =
        !filters.subcompanyId ||
        Number(r.companyAdminId) === Number(filters.subcompanyId)

      const byVessel =
        !filters.vesselId ||
        Number(r.vesselId) === Number(filters.vesselId)

      const byStatus =
        filters.status === 'All' ||
        r.status === (filters.status as FindingStatus)

      const byFindingType =
        filters.findingType === 'All' ||
        !r.findingType ||
        r.findingType === (filters.findingType as FindingType)

      const byFromDate =
        !filters.fromDate ||
        (!r.findingDate || new Date(r.findingDate) >= new Date(filters.fromDate))

      const byToDate =
        !filters.toDate ||
        (!r.findingDate || new Date(r.findingDate) <= new Date(filters.toDate))

      return (
        matchesSearch &&
        byCompany &&
        bySubcompany &&
        byVessel &&
        byStatus &&
        byFindingType &&
        byFromDate &&
        byToDate
      )
    })
  }, [records, searchTerm, filters])

  const sortedData = useMemo(() => {
    const data = [...filteredData]
    if (!sortColumn) return data

    data.sort((a, b) => {
      const aVal = a[sortColumn]
      const bVal = b[sortColumn]

      if (sortColumn === 'findingDate') {
        const aTime = aVal ? new Date(String(aVal)).getTime() : 0
        const bTime = bVal ? new Date(String(bVal)).getTime() : 0
        const diff = aTime - bTime
        return sortOrder === 'asc' ? diff : -diff
      }

      const aStr = (aVal ?? '').toString().toLowerCase()
      const bStr = (bVal ?? '').toString().toLowerCase()
      if (aStr < bStr) return sortOrder === 'asc' ? -1 : 1
      if (aStr > bStr) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return data
  }, [filteredData, sortColumn, sortOrder])

  const showVesselColumn = !filters.vesselId
  const columnCount = showVesselColumn ? 13 : 12

  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const currentRecords = sortedData.slice(indexOfFirstRecord, indexOfLastRecord)
  const totalPages = Math.ceil(sortedData.length / rowsPerPage)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page)
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setCurrentPage(1)
  }

  const handleSort = (col: keyof AuditFindingRecord) => {
    setSortColumn(col)
    setSortOrder(prev =>
      sortColumn === col && prev === 'asc' ? 'desc' : 'asc'
    )
  }

  // -----------------------
  // Modals: view / edit / add
  // -----------------------
  const openView = async (id: number) => {
    try {
      const dto = await getAuditFinding(id)
      setViewing(dto)
      setViewOpen(true)
    } catch (e) {
      console.error('Failed to get finding', e)
      toastApiError(e, 'Failed to load audit finding')
    }
  }

  const openEdit = async (id: number) => {
    try {
      const dto = await getAuditFinding(id)
      setEditing(dto)
      setEditOpen(true)
    } catch (e) {
      console.error('Failed to get finding', e)
      toastApiError(e, 'Failed to load audit finding')
    }
  }

  const handleDeleteClick = (r: AuditFindingRecord) => {
    setFindingToDelete(r)
    setDeleteModalVisible(true)
  }

  const handleConfirmDelete = async () => {
    if (!findingToDelete) return
    setDeleteLoading(true)
    try {
      await deleteAuditFinding(findingToDelete.id)
      toast.success('Audit finding deleted successfully', {position: 'top-center'})
      setDeleteModalVisible(false)
      setFindingToDelete(null)
      await loadFindings()
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete audit finding'
      toast.error(msg, {position: 'top-center'})
    } finally {
      setDeleteLoading(false)
    }
  }

  // Close / reopen a finding (drives audit completed counts)
  const handleToggleClosed = async (r: AuditFindingRecord) => {
    const closing = !r.isClosed
    const msg = closing
      ? 'Mark this finding as completed / closed?'
      : 'Reopen this finding?'
    if (!window.confirm(msg)) return
    try {
      if (closing) {
        await closeAuditFinding(r.id)
      } else {
        await reopenAuditFinding(r.id)
      }
      toast.success(closing ? 'Finding closed successfully' : 'Finding reopened successfully', {
        position: 'top-center',
      })
      await loadFindings()
    } catch (e) {
      console.error('Failed to update finding close state', e)
      toastApiError(e, closing ? 'Failed to close finding' : 'Failed to reopen finding')
    }
  }

  const handleSubmitFinding = async (r: AuditFindingRecord) => {
    if (submittingActionId === r.id) return
    try {
      setSubmittingActionId(r.id)
      await submitAuditFinding(r.id)
      toast.success('Finding submitted for shore approval successfully', {position: 'top-center'})
      await loadFindings()
    } catch (e: any) {
      console.error('Failed to submit finding', e)
      toastApiError(e, 'Failed to submit finding')
    } finally {
      setSubmittingActionId(null)
    }
  }

  const openApprovalModal = (r: AuditFindingRecord, action: ApprovalAction) => {
    setApprovalTargetFinding(r)
    setApprovalAction(action)
    setApprovalModalOpen(true)
  }

  const handleConfirmApproval = async (shoreRemarks: string, rejectionRemark: string) => {
    if (!approvalTargetFinding) return
    setApprovalLoading(true)
    try {
      if (approvalAction === 'approve') {
        await approveAuditFinding(approvalTargetFinding.id, shoreRemarks)
        toast.success('Finding approved successfully', {position: 'top-center'})
      } else {
        await rejectAuditFinding(approvalTargetFinding.id, rejectionRemark, shoreRemarks)
        toast.success('Finding rejected successfully', {position: 'top-center'})
      }
      setApprovalModalOpen(false)
      await loadFindings()
    } catch (e) {
      console.error(`Failed to ${approvalAction} finding`, e)
      toastApiError(e, `Failed to ${approvalAction} finding`)
    } finally {
      setApprovalLoading(false)
    }
  }


  const openAttachmentsModal = async (rec: AuditFindingRecord | QHSEAuditFindingDto) => {
    const id = Number(rec.id)
    const label =
      (rec as any).findingLabel ||
      (rec as any).findingName ||
      `#${id}`

    setAttachmentsModal({
      visible: true,
      findingId: id,
      findingLabel: label,
      loading: true,
      items: [],
    })

    try {
      const list: AuditFindingAttachmentInfo[] = await listAuditFindingAttachments(id)
      const items = list.map(it => ({
        fileName: it.fileName,
        viewUrl: AuditFindingAttachmentFileViewUrl(id, it.fileName),
        downloadUrl: AuditFindingAttachmentFileDownloadUrl(id, it.fileName),
      }))

      setAttachmentsModal(prev => ({
        ...prev,
        loading: false,
        items,
      }))
    } catch (e) {
      console.error('Failed to load finding attachments', e)
      toastApiError(e, 'Failed to load attachments')
      setAttachmentsModal(prev => ({...prev, loading: false}))
    }
  }

  const handleAddFinding = async (form: AuditFindingSubmitShape) => {
    if (creatingFinding) return
    const payload: Partial<QHSEAuditFindingDto> & {
      auditId: number
      findingName: string
      status: FindingStatus
    } = {
      auditId: Number(form.auditId),
      findingName: form.findingName,
      description: form.description,
      // no findingDate – date comes from audit itself
      isPositive:
        form.critical === 'YES' ? true :
        form.critical === 'NO' ? false : undefined,
      remarks: form.remarks || undefined,
      correctiveAction: form.correctiveAction || undefined,
      preventiveAction: form.preventiveAction || undefined,
      status: form.status as FindingStatus,
      findingType: form.findingType as any,
      active: true,
    }

    try {
      setCreatingFinding(true)
      const created = await createAuditFinding(payload)
      const id = Number(created.id)
      if (id && form.attachmentFiles && form.attachmentFiles.length > 0) {
        await uploadAuditFindingAttachments(id, form.attachmentFiles)
      }
      toast.success('Audit finding created successfully', {position: 'top-center'})
      setAddOpen(false)
      await loadFindings()
    } catch (e: any) {
      console.error('Create audit finding failed', e)
      toastApiError(e, 'Failed to create finding')
    } finally {
      setCreatingFinding(false)
    }
  }

  const handleUpdateFinding = async (form: AuditFindingSubmitShape) => {
    if (!editing?.id) return

    const partial: Partial<QHSEAuditFindingDto> = {
      auditId: Number(form.auditId),
      findingName: form.findingName,
      description: form.description,
      // no findingDate – audit date is already tied to the audit
      isPositive:
        form.critical === 'YES' ? true :
        form.critical === 'NO' ? false : undefined,
      remarks: form.remarks || undefined,
      correctiveAction: form.correctiveAction || undefined,
      preventiveAction: form.preventiveAction || undefined,
      status: form.status as FindingStatus,
      findingType: form.findingType as any,
    }

    try {
      const updated = await updateAuditFinding(editing.id, partial)
      const id = Number(updated.id)
      if (id && form.attachmentFiles && form.attachmentFiles.length > 0) {
        await uploadAuditFindingAttachments(id, form.attachmentFiles)
      }

      toast.success('Audit finding updated successfully', {position: 'top-center'})
      setEditOpen(false)
      setEditing(null)
      await loadFindings()
    } catch (e: any) {
      console.error('Update audit finding failed', e)
      toastApiError(e, 'Failed to update finding')
    }
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            {/* Header */}
            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>Audit Findings</h3>
              </div>
              <div className='card-toolbar'>
                {canCreateFinding ? (
                  <button
                    type='button'
                    className='btn btn_primary'
                    onClick={() => setAddOpen(true)}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                    Add New Finding
                  </button>
                ) : null}
              </div>
            </div>

            {/* Filters */}
            <div className='card-body py-4 bg-white border-top'>
              <div className='row gx-3 gy-3 mb-4'>

                {/* Status */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Status
                  </label>
                  <SearchableSelect
                    className='form-select'
                    name='status'
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <option value='All'>All</option>
                    <option value='SATISFACTORY'>Satisfactory</option>
                    <option value='NON_CRITICAL'>Non-critical</option>
                    <option value='CRITICAL'>Critical</option>
                    <option value='OBSERVATION'>Observation</option>
                    <option value='OPPORTUNITY_FOR_IMPROVEMENT'>Opportunity for improvement</option>
                  </SearchableSelect>
                </div>

                {/* Finding Type */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Finding Type
                  </label>
                  <SearchableSelect
                    className='form-select'
                    name='findingType'
                    value={filters.findingType}
                    onChange={handleFilterChange}
                  >
                    <option value='All'>All</option>
                    <option value='NON_CONFORMITY_REPORT'>Non conformity report</option>
                    <option value='OBSERVATION_NOTE'>Observation note</option>
                    <option value='FAILURE_NOTE'>Failure note</option>
                    <option value='DEVIATION_NOTE'>Deviation note</option>
                  </SearchableSelect>
                </div>

                {/* Search */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Search
                  </label>
                  <input
                    type='text'
                    className='form-control'
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder='Vessel / audit / finding'
                  />
                </div>

                {/* Clear */}
                <div className='col-md-2 d-flex align-items-end'>
                  <button
                    type='button'
                    className='btn btn-light'
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </button>
                </div>
              </div>

              <div className='row gx-3 gy-3 mb-4'>
                {/* From Date */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    From Date
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    name='fromDate'
                    value={filters.fromDate}
                    onChange={handleFilterChange}
                  />
                </div>

                {/* To Date */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    To Date
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    name='toDate'
                    value={filters.toDate}
                    onChange={handleFilterChange}
                  />
                </div>
              </div>

              {/* Table */}
              <div className='report-table table-responsive' style={{position: 'relative'}}>
                {loading && <div style={tableOverlayStyle}>{spinner}</div>}
                <div style={{overflowX: 'auto'}}>
                  <table className='table table-bordered align-middle'>
                    <thead className='table-header text-start'>
                      <tr>
                        {/* Sr/No */}
                        <th className='text-center text-nowrap' style={{minWidth: '70px'}}>
                          Sr/No
                        </th>

                        
                        {/* Finding Name */}
                        <th style={{minWidth: '180px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Finding Name</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('findingLabel')}
                              disabled={records.length === 0}
                            > 
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'findingLabel'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>


                        {/* Vessel */}
                        {showVesselColumn && (
                          <th style={{minWidth: '180px'}}>
                            <div className='d-flex align-items-center text-nowrap'>
                              <span className='me-1'>Vessel</span>
                              <button
                                type='button'
                                className='btn btn-link p-0 m-0 pb-1'
                                onClick={() => handleSort('vesselName')}
                                disabled={records.length === 0}
                              >
                                <KTSVG
                                  path={`/media/map/sort-col-${
                                    sortColumn === 'vesselName'
                                      ? sortOrder === 'asc'
                                        ? 'up-black'
                                        : 'down-black'
                                      : 'grey'
                                  }.svg`}
                                  className='svg-icon-3'
                                />
                              </button>
                            </div>
                          </th>
                        )}

                        {/* Audit */}
                        <th style={{minWidth: '200px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Audit</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('auditName')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'auditName'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>

                        {/* Finding Details */}
                        <th style={{minWidth: '260px'}}>Finding Details</th>

                        {/* Critical */}
                        <th style={{minWidth: '100px'}}>Critical</th>

                        {/* Status */}
                        <th style={{minWidth: '140px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Status</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('status')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'status'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>

                        {/* Finding Type */}
                        <th style={{minWidth: '160px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Finding Type</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('findingType')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'findingType'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>

                        {/* Completed */}
                        <th className='text-center' style={{minWidth: '120px'}}>Completed</th>

                        {/* Workflow Status */}
                        <th style={{minWidth: '140px'}}>Workflow Status</th>

                        {/* Audit Date */}
                        <th style={{minWidth: '140px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Audit Date</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('findingDate')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'findingDate'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>

                        {/* Attachment */}
                        <th style={{minWidth: '130px'}}>Attachment</th>

                        {/* Actions */}
                        <th style={{minWidth: '130px', textAlign: 'center'}}>Actions</th>
                      </tr>
                    </thead>

                    <tbody className='table-body text-start'>
                      {(bootLoading || loading) ? (
                        // skeleton
                        Array.from({length: 8}).map((_, i) => (
                          <tr key={`skeleton-${i}`}>
                            {Array.from({length: columnCount}).map((__, c) => (
                              <td key={`s-${i}-${c}`} className='py-3'>
                                <div className='placeholder-wave'>
                                  <div className='placeholder w-100' style={{height: 14, borderRadius: 4}} />
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (currentRecords.length === 0 && noDataDelayPassed) ? (
                        <tr>
                          <td colSpan={columnCount} className='text-center py-5 text-muted'>
                            No audit findings found for the selected criteria.
                          </td>
                        </tr>
                      ) : (
                        currentRecords.map((r, idx) => (
                          <tr key={r.id}>
                            {/* Sr/No */}
                            <td className='text-center'>
                              {indexOfFirstRecord + idx + 1}
                            </td>

                            
                            {/* Finding Label */}
                            <td className='text-nowrap'>
                              {r.findingLabel || '-'}
                            </td>

                            {/* Vessel */}
                            {showVesselColumn && (
                              <td className='fw-bold text-nowrap'>
                                {r.vesselName || '-'}
                              </td>
                            )}

                            {/* Audit */}
                            <td className='text-nowrap'>
                              {r.auditName || '-'}
                            </td>


                            {/* Finding Details */}
                            <td>
                              <span
                                title={r.findingName || '-'}
                                style={{
                                  maxWidth: '260px',
                                  display: 'inline-block',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {r.findingName || '-'}
                              </span>
                            </td>

                            {/* Critical */}
                            <td className='text-center'>
                              {r.critical === true ? 'Yes' : r.critical === false ? 'No' : '-'}
                            </td>

                            {/* Status */}
                            <td className='text-nowrap'>
                              {r.status ? (
                                <span className={`badge ${statusBadgeClass(r.status)}`}>
                                  {niceEnum(r.status)}
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>

                            {/* Finding Type */}
                            <td className='text-nowrap'>
                              {r.findingType ? niceEnum(r.findingType) : '-'}
                            </td>

                            {/* Completed */}
                            <td className='text-center text-nowrap'>
                              {r.isClosed ? (
                                <span className='badge badge-light-success' title={r.closedAt ? `Closed at ${String(r.closedAt).substring(0, 10)}` : undefined}>
                                  Completed
                                </span>
                              ) : (
                                <span className='badge badge-light-warning'>Open</span>
                              )}
                            </td>

                            {/* Workflow Status */}
                            <td className='text-nowrap'>
                              <span className={`badge ${
                                r.workflowStatus === 'APPROVED' ? 'badge-light-success' :
                                r.workflowStatus === 'PENDING_APPROVAL' ? 'badge-light-warning' :
                                r.workflowStatus === 'REJECTED' ? 'badge-light-danger' :
                                'badge-light-secondary'
                              }`}>
                                {r.workflowStatus ? niceEnum(r.workflowStatus) : 'Draft'}
                              </span>
                            </td>


                            {/* Audit Date */}
                            <td className='text-nowrap'>
                              {r.findingDate || '-'}
                            </td>

                            {/* Attachment */}
                            <td className='text-center'>
                              {r.attachmentPath ? (
                                canOpenFindingFiles ? (
                                  <button
                                    type='button'
                                    className='btn btn-light-primary btn-sm d-inline-flex align-items-center text-nowrap'
                                    onClick={() => openAttachmentsModal(r)}
                                  >
                                    <KTSVG
                                      path='/media/icons/duotune/files/fil003.svg'
                                      className='svg-icon-3 me-1'
                                    />
                                    View Files
                                  </button>
                                ) : (
                                  '-'
                                )
                              ) : (
                                '-'
                              )}
                            </td>

                            {/* Actions */}
                            <td style={{textAlign: 'left'}} className='text-nowrap'>
                              <div className='d-flex justify-content-left gap-1'>
                                {canReadFinding ? (
                                  <button
                                    className='btn btn-icon btn-sm'
                                    title='View'
                                    onClick={() => openView(r.id)}
                                  >
                                    <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
                                  </button>
                                ) : null}

                                {canUpdateFinding ? (
                                  <button
                                    className='btn btn-icon btn-sm'
                                    title='Edit'
                                    onClick={() => openEdit(r.id)}
                                  >
                                    <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
                                  </button>
                                ) : null}

                                {/* Submit button */}
                                {canSubmitFinding && (r.workflowStatus === 'DRAFT' || r.workflowStatus === 'REJECTED' || !r.workflowStatus) ? (
                                  <button
                                    className='btn btn-sm btn-light-primary py-1 px-2 text-nowrap'
                                    title='Submit finding for shore approval'
                                    onClick={() => handleSubmitFinding(r)}
                                    disabled={submittingActionId === r.id || creatingFinding}
                                  >
                                    {submittingActionId === r.id ? 'Submitting...' : 'Submit'}
                                  </button>
                                ) : null}

                                {/* Approve button */}
                                {canApproveFinding && r.workflowStatus === 'PENDING_APPROVAL' ? (
                                  <button
                                    className='btn btn-sm btn-light-success py-1 px-2 text-nowrap'
                                    title='Approve finding'
                                    onClick={() => openApprovalModal(r, 'approve')}
                                  >
                                    Approve
                                  </button>
                                ) : null}

                                {/* Reject button */}
                                {canRejectFinding && r.workflowStatus === 'PENDING_APPROVAL' ? (
                                  <button
                                    className='btn btn-sm btn-light-danger py-1 px-2 text-nowrap'
                                    title='Reject finding'
                                    onClick={() => openApprovalModal(r, 'reject')}
                                  >
                                    Reject
                                  </button>
                                ) : null}

                                {/* Delete button */}
                                {canDeleteFinding ? (
                                  <button
                                    className='btn btn-icon btn-sm'
                                    title='Delete'
                                    onClick={() => handleDeleteClick(r)}
                                  >
                                    <KTSVG path='/media/map/trash.svg' className='svg-icon-3' />
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div
                  className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top'
                  style={{
                    position: 'static',
                    bottom: 0,
                    backgroundColor: '#fff',
                    zIndex: 10,
                  }}
                >
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-2'>Rows per page</span>
                    <SearchableSelect
                      className='form-select'
                      style={{
                        borderRadius: '20px',
                        width: '70px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        padding: '4px 8px',
                      }}
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </SearchableSelect>
                  </div>

                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-3' style={{fontSize: '14px'}}>
                      Showing{' '}
                      <strong>
                        {((currentPage - 1) * rowsPerPage) + 1}-
                        {Math.min(currentPage * rowsPerPage, sortedData.length)}
                      </strong>{' '}
                      of <strong>{sortedData.length}</strong>
                    </span>

                    <nav>
                      <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>
                        </li>

                        {(() => {
                          const pages = []
                          const showPages = 5
                          let startPage = Math.max(1, currentPage - 2)
                          let endPage = Math.min(totalPages, startPage + showPages - 1)

                          if (endPage - startPage < showPages - 1) {
                            startPage = Math.max(1, endPage - showPages + 1)
                          }

                          if (startPage > 1) {
                            pages.push(
                              <li key={1} className='page-item'>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                  }}
                                  onClick={() => handlePageChange(1)}
                                >
                                  1
                                </button>
                              </li>
                            )

                            if (startPage > 2) {
                              pages.push(
                                <li key='ellipsis1' className='page-item disabled'>
                                  <span
                                    className='page-link border-0 text-muted'
                                    style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                  >
                                    ...
                                  </span>
                                </li>
                              )
                            }
                          }

                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <li
                                key={i}
                                className={`page-item ${currentPage === i ? 'active' : ''}`}
                              >
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    boxShadow: 'none',
                                  }}
                                  onClick={() => handlePageChange(i)}
                                >
                                  {i}
                                </button>
                              </li>
                            )
                          }

                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <li key='ellipsis2' className='page-item disabled'>
                                  <span
                                    className='page-link border-0 text-muted'
                                    style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                  >
                                    ...
                                  </span>
                                </li>
                              )
                            }

                            pages.push(
                              <li key={totalPages} className='page-item'>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                  }}
                                  onClick={() => handlePageChange(totalPages)}
                                >
                                  {totalPages}
                                </button>
                              </li>
                            )
                          }

                          return pages
                        })()}

                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            ›
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Add Finding */}
      <AddAuditFindingModal
        visible={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAddFinding}
        audits={auditOptions}
        vessels={vesselsForFilters}
        vesselId={addVesselId}
        onVesselChange={setAddVesselId}
        showVesselSelect={!shipboardUser}
        auditDisabled={!shipboardUser && !addVesselId}
        auditsLoading={auditOptionsLoading}
      />

      {/* View Finding */}
      <ViewAuditFindingModal
        visible={viewOpen}
        onClose={() => {
          setViewOpen(false)
          setViewing(null)
        }}
        record={viewing}
        onOpenAttachments={canOpenFindingFiles ? rec => openAttachmentsModal(rec) : undefined}
      />

      {/* Edit Finding */}
      <EditAuditFindingModal
        visible={editOpen}
        onClose={() => {
          setEditOpen(false)
          setEditing(null)
        }}
        record={editing}
        audits={auditOptions}
        onSubmit={handleUpdateFinding}
      />

      {/* Shared File Viewer */}
      <FileViewerModal
        visible={fileModal.visible}
        onClose={() => setFileModal(prev => ({...prev, visible: false}))}
        title={fileModal.title}
        fileName={fileModal.fileName}
        viewUrl={fileModal.viewUrl}
        downloadUrl={fileModal.downloadUrl}
      />

      {/* Finding Approval Action Modal */}
      <FindingApprovalActionModal
        visible={approvalModalOpen}
        action={approvalAction}
        findingName={approvalTargetFinding?.findingLabel || approvalTargetFinding?.findingName}
        onClose={() => setApprovalModalOpen(false)}
        onConfirm={handleConfirmApproval}
        isLoading={approvalLoading}
      />

      <AuditFindingAttachmentsModal
        visible={attachmentsModal.visible}
        findingLabel={attachmentsModal.findingLabel}
        loading={attachmentsModal.loading}

        items={attachmentsModal.items}
        onClose={() =>
          setAttachmentsModal(prev => ({...prev, visible: false}))
        }
        onViewFile={item => {
          setFileModal({
            visible: true,
            title: 'Finding Attachment',
            fileName: item.fileName,
            viewUrl: item.viewUrl,
            downloadUrl: item.downloadUrl,
          })
        }}
      />

      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: "5rem" }}
      />

      <DeleteRecordConfirmModal
        visible={deleteModalVisible}
        recordType='Audit Finding'
        vesselName={findingToDelete?.vesselName}
        loading={deleteLoading}
        onClose={() => {
          setDeleteModalVisible(false)
          setFindingToDelete(null)
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}

export default AuditFindingIndex
